import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { resetPassword, logout } from '../api/userDataService';
import { isValidPassword, passwordError, PASSWORD_RULES_TEXT } from '../utils/passwordPolicy';

// Landing page for the "choose your password" link in the welcome email. The
// link carries the userId + single-use token; redemption goes through the same
// endpoint as a password reset, which validates the token, enforces the
// complexity rules and deletes the token afterwards.
const RULES = PASSWORD_RULES_TEXT;

function SetPassword() {
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const linkValid = !!uid && !!token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Those passwords don’t match.');
      return;
    }

    // Checked here so the rule is named before a round trip; the server applies
    // the same one on redemption.
    if (!isValidPassword(password)) {
      setError(passwordError(password));
      return;
    }

    setLoading(true);
    try {
      await resetPassword(uid, token, password, confirm);

      // The link is often opened in a browser that still holds another
      // account's session — a second account, or a shared machine. Without
      // clearing it, /login sees isAuthenticated and bounces to /homepage as
      // the OLD user, who then appears to have "become" the new one.
      logout();

      setDone(true);
      // Give them a beat to read the confirmation before the login screen.
      // A full page load, not navigate(): isAuthenticated is App-level state
      // read at mount, so clearing storage alone leaves the stale guard in
      // place and /login would still redirect away.
      setTimeout(() => window.location.replace('/login'), 2500);
    } catch (err) {
      setError(err?.message || 'Could not set your password. The link may have expired.');
      setLoading(false);
    }
  };

  const field = {
    width: '100%',
    padding: '11px 12px',
    borderRadius: 'var(--r-sm)',
    border: '1px solid var(--line-2)',
    background: 'var(--surface-sunk)',
    color: 'var(--ink)',
    fontSize: '0.9375rem',
  };

  return (
    <div
      className="rd"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
        padding: '2rem 1.25rem',
      }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420, padding: 'clamp(1.5rem, 6vw, 2.25rem)' }}>
        {done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={40} color="var(--accent)" strokeWidth={1.75} />
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ink)', margin: '1rem 0 0.5rem' }}>
              Password set
            </h1>
            <p style={{ color: 'var(--ink-2)', fontSize: '0.9375rem', margin: 0 }}>
              Taking you to the sign-in page…
            </p>
          </div>
        ) : !linkValid ? (
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ink)', margin: '0 0 0.5rem' }}>
              This link isn’t valid
            </h1>
            <p style={{ color: 'var(--ink-2)', fontSize: '0.9375rem', margin: '0 0 1.25rem' }}>
              It looks incomplete. Use the most recent email we sent, or reset your
              password from the sign-in page.
            </p>
            <Link className="btn btn-primary" to="/login" style={{ textDecoration: 'none' }}>
              Go to sign in
            </Link>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <KeyRound size={28} color="var(--accent)" strokeWidth={1.75} />
              <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ink)', margin: '0.75rem 0 0.375rem' }}>
                Choose your password
              </h1>
              <p style={{ color: 'var(--ink-2)', fontSize: '0.875rem', margin: 0 }}>{RULES}</p>
            </div>

            <form onSubmit={handleSubmit}>
              <label htmlFor="new-password" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
                New password
              </label>
              <div style={{ position: 'relative', marginBottom: '1rem' }}>
                <input
                  id="new-password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  style={{ ...field, paddingRight: 44 }}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', top: 0, right: 0, height: '100%', width: 42,
                    background: 'none', border: 'none', color: 'var(--ink-3)', cursor: 'pointer',
                  }}
                >
                  {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>

              <label htmlFor="confirm-password" style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
                Confirm password
              </label>
              <input
                id="confirm-password"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                style={{ ...field, marginBottom: '1.25rem' }}
              />

              {error && (
                <p role="alert" style={{ color: 'var(--danger)', fontSize: '0.85rem', lineHeight: 1.5, margin: '0 0 1rem' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !password || !confirm}
                style={{ width: '100%', justifyContent: 'center', padding: '12px 16px', opacity: loading ? 0.6 : 1 }}
              >
                {loading ? 'Setting password…' : 'Set password and sign in'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default SetPassword;
